import type { Dayjs } from 'dayjs';

import {
  mapToCreateBookingVoucher,
  mapToUpdateBookingVoucher,
} from 'app/hooks/data/core/admin/bookings/mapping/form';
import { useCreateBookingVoucher } from 'app/hooks/data/core/admin/bookings/useCreateBookingVoucher';
import { useDeleteBookingVoucher } from 'app/hooks/data/core/admin/bookings/useDeleteBookingVoucher';
import { useUpdateBookingVoucher } from 'app/hooks/data/core/admin/bookings/useUpdateBookingVoucher';
import type {
  BookingDetails,
  BookingVoucherResponse,
} from 'app/typings/core/admin/bookings/booking';

export type VoucherFormValues = {
  value: number;
  expirationDate: Dayjs;
  reasonType?: string;
  comment?: string;
};

type UseVoucherActionsParams = {
  bookingDetails: BookingDetails;
  voucher?: BookingVoucherResponse;
};

export function useVoucherActions({
  bookingDetails,
  voucher,
}: UseVoucherActionsParams) {
  const doesVoucherExist = !!voucher?.code;

  const { mutateAsync: createVoucher, isLoading: isCreating } =
    useCreateBookingVoucher();
  const { mutateAsync: updateVoucher, isLoading: isUpdating } =
    useUpdateBookingVoucher(bookingDetails.code, voucher?.code);
  const { mutateAsync: deleteVoucher, isLoading: isDeleting } =
    useDeleteBookingVoucher(bookingDetails.code, voucher?.code);

  const onSave = async (values: VoucherFormValues) => {
    const payload = {
      value: values.value,
      expirationDate: values.expirationDate,
      reasonType: values.reasonType,
      comment: values.comment,
    };

    if (doesVoucherExist) {
      await updateVoucher(mapToUpdateBookingVoucher(payload));
    } else {
      await createVoucher({
        code: bookingDetails.code,
        payload: mapToCreateBookingVoucher(payload, bookingDetails),
      });
    }
  };

  const onDelete = async () => {
    await deleteVoucher();
  };

  return {
    doesVoucherExist,
    onSave,
    onDelete,
    isMutating: isCreating || isUpdating || isDeleting,
  };
}
